import React, { useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { IoIosArrowRoundForward } from "react-icons/io";
import { FaFacebookF, FaInstagram } from "react-icons/fa";

import HeroPng from "../../assets/hero.png";
import bg from "../../assets/bg.png";
import frame from "../../assets/frame.png";
import { MotionUp } from "../../utility/animation.js";
import { trackCustomEvent } from "../../pixel.js";

/* ——— Social ——— */
const SocialLinks = memo(function SocialLinks() {
  return (
    <motion.div
      variants={MotionUp(0.8)}
      initial="initial"
      animate="animate"
      className="flex justify-center md:justify-start mt-10 space-x-4"
      aria-label="Réseaux sociaux"
    >
      <a
        href="https://www.facebook.com/Scholine"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Visitez notre page Facebook (nouvel onglet)"
        className="bg-white/80 backdrop-blur p-3 rounded-full hover:bg-white transition shadow-sm"
      >
        <FaFacebookF aria-hidden="true" className="text-blue-600" />
      </a>
      <a
        href="https://www.instagram.com/scho_line/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Visitez notre page Instagram (nouvel onglet)"
        className="bg-white/80 backdrop-blur p-3 rounded-full hover:bg-white transition shadow-sm"
      >
        <FaInstagram aria-hidden="true" className="text-pink-500" />
      </a>
    </motion.div>
  );
});

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2">
    <span
      aria-hidden="true"
      className="mt-1.5 inline-block h-2 w-2 rounded-full bg-secondary"
    />
    <span className="text-gray-700">{children}</span>
  </li>
);

const Hero = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const handleRoute = useCallback(
    (path, label) => {
      trackCustomEvent("HeroCTAClick", { label });
      navigate(path);
    },
    [navigate]
  );

  const handleLoginClick = useCallback(() => {
    trackCustomEvent("HeroCTAClick", { label: "login_cta" });
    window.location.href = "/login";
  }, [navigate]);

  const heroImgInitial = prefersReducedMotion ? {} : { y: 24, opacity: 0 };
  const heroImgAnimate = prefersReducedMotion ? {} : { y: 0, opacity: 1 };
  const heroImgTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.6, delay: 0.25, ease: "easeOut" };

  return (
    <div>
      {/* <Navbar /> */}

      <section
        className="relative w-full min-h-screen md:h-screen bg-cover bg-center"
        style={{ backgroundImage: `url(${bg})` }}
        aria-label="Section d’accueil"
      >
        {/* Scrim: stronger on the left so text always pops */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/10"
        />

        <div className="container relative z-10 grid grid-cols-1 md:grid-cols-[1.1fr_1fr] items-center gap-8 lg:gap-12 pt-24 md:pt-28">
          {/* Content FIRST (prevents hiding) */}
          <div className="order-1 flex flex-col justify-center items-center md:items-start px-4 md:px-0">
            {/* On small screens, give the text a soft card for readability */}
            <div className="w-full md:w-auto md:bg-transparent bg-white/80 backdrop-blur-md rounded-xl md:rounded-none shadow-sm md:shadow-none p-4 md:p-0">
              {/* Offer badge */}
              <motion.div
                variants={MotionUp(0.35)}
                initial="initial"
                animate="animate"
                className="mb-3"
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-secondary/30 bg-secondary/10 px-3 py-1 text-secondary text-xs sm:text-sm font-medium">
                  -10% aujourd’hui{" "}
                  <span className="hidden sm:inline text-gray-600">
                    Offre de bienvenue
                  </span>
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                variants={MotionUp(0.5)}
                initial="initial"
                animate="animate"
                className="text-left text-[clamp(28px,5vw,48px)] font-bold leading-tight text-primary"
              >
                Digitalisez votre école.
                <br />
                Boostez votre gestion.
              </motion.h1>

              {/* Subhead */}
              <motion.p
                variants={MotionUp(0.6)}
                initial="initial"
                animate="animate"
                className="mt-3 text-gray-700 text-[clamp(14px,2.2vw,18px)] max-w-xl"
              >
                Centralisez <strong>admissions</strong>,{" "}
                <strong>paiements</strong> et <strong>communication</strong>{" "}
                dans une plateforme moderne, sécurisée et simple.
              </motion.p>

              {/* Value points */}
              <motion.ul
                variants={MotionUp(0.7)}
                initial="initial"
                animate="animate"
                className="mt-5 grid gap-2 text-sm sm:text-base"
              >
                <Bullet>Formulaires en ligne + suivi en temps réel</Bullet>
                <Bullet>Paiements sécurisés & reçus automatiques</Bullet>
                <Bullet>Espace parent/élève avec notifications</Bullet>
                <Bullet>Tableau de bord clair pour votre équipe</Bullet>
              </motion.ul>

              {/* CTAs */}
              <motion.div
                variants={MotionUp(0.8)}
                initial="initial"
                animate="animate"
                className="mt-6 flex w-full md:w-auto justify-start gap-3 sm:gap-4"
                role="group"
                aria-label="Actions principales"
              >
                <button
                  type="button"
                  onClick={() => handleRoute("/register", "register_cta")}
                  className="primary-btn group flex items-center gap-2 py-2.5 px-4 sm:px-5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Commencer gratuitement
                  <IoIosArrowRoundForward
                    aria-hidden="true"
                    className="text-xl transition-transform group-hover:translate-x-2 group-hover:-rotate-45 duration-300"
                  />
                </button>

                <button
                  type="button"
                  onClick={handleLoginClick}
                  className="secondary-btn group flex items-center gap-2 py-2.5 px-4 sm:px-5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                >
                  Se connecter
                  <IoIosArrowRoundForward
                    aria-hidden="true"
                    className="text-xl transition-transform group-hover:translate-x-2 group-hover:-rotate-45 duration-300"
                  />
                </button>
              </motion.div>

              {/* Micro-trust */}
              <motion.p
                variants={MotionUp(0.9)}
                initial="initial"
                animate="animate"
                className="mt-2 text-xs sm:text-sm text-gray-500"
              >
                Déjà adoptée par des écoles et centres de formation. Aucun
                engagement.
              </motion.p>
            </div>

            {/* Social */}
            <SocialLinks />
          </div>

          {/* Image SECOND, sandboxed to never overlap text */}
          <div className="order-2 relative px-4 md:px-0">
            <div className="relative mx-auto md:ml-auto w-full max-w-[560px]">
              {/* Keep the image inside a ratio box so it can’t spill over */}
              <div className="relative w-full aspect-[4/3] lg:aspect-[16/12]">
                <motion.img
                  initial={heroImgInitial}
                  animate={heroImgAnimate}
                  transition={heroImgTransition}
                  src={HeroPng}
                  alt="Aperçu de la plateforme Scholine"
                  className="absolute inset-0 h-full w-full object-contain drop-shadow-xl select-none pointer-events-none"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>

            {/* QR brochure (desktop) */}
            <motion.div
              variants={MotionUp(1.0)}
              initial="initial"
              animate="animate"
              className="hidden lg:flex justify-end mt-6"
            >
              <a
                href="https://www.papermark.com/view/cmfb6soj20009l804p4tugajd"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ouvrir la brochure (nouvel onglet)"
                className="inline-flex items-center gap-4 bg-white/70 backdrop-blur-md px-4 py-3 rounded-xl shadow-md hover:bg-white transition"
              >
                <img
                  src={frame}
                  alt="QR code vers la brochure Scholine"
                  className="h-24 w-28"
                  loading="lazy"
                  decoding="async"
                />
                <div className="text-left">
                  <p className="text-sm font-semibold text-primary">
                    Découvrir la brochure
                  </p>
                  <p className="text-xs text-gray-600">
                    Fonctionnalités, tarifs & cas d’usage
                  </p>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;
